import { Controller } from "@hotwired/stimulus"

const VALID_MIME_PREFIXES = ['image/', 'video/', 'application/pdf']


export default class extends Controller {
  static targets = [
    "modal",
    "form",
    "dropzone",
    "fileInput",
    "filePreview",
    "fileName",
    "submitButton",
    "progressContainer",
    "progressBar",
    "statusMessage",
    "ticketDisplay",
    "ticketInput",
    "ticketSelector",
    "ticketInfo",
    "commentInput",
    "ticketSearch",
    "commentPreview",
    "commentTab",
    "writeTab"
  ]

  static values = {
    ticketId: Number,
    ticketUid: String,
    allowedExtensions: String
  }

  connect() {
    this.selectedFile = null
    this.searchTimeout = null

    // Register this controller globally so it can be accessed from outside
    window.openUploadModal = (ticketId, ticketUid) => {
      this.openModalWithTicket(ticketId, ticketUid)
    }
  }

  disconnect() {
    // Clean up global function
    delete window.openUploadModal
  }

  openModalWithTicket(ticketId, ticketUid) {
    this.ticketIdValue = ticketId
    this.ticketUidValue = ticketUid

    this.ticketDisplayTarget.textContent = ticketUid
    this.ticketInputTarget.value = ticketId

    // Show ticket info box, hide search input
    if (this.hasTicketInfoTarget) this.ticketInfoTarget.classList.remove("hidden")
    if (this.hasTicketSelectorTarget) this.ticketSelectorTarget.classList.add("hidden")

    this.modalTarget.classList.remove("hidden")
    this.resetForm()
  }

  openModal(event) {
    event.preventDefault()

    // Set ticket info from the button's data attributes
    const ticketId = event.currentTarget.dataset.uploadTicketIdValue
    const ticketUid = event.currentTarget.dataset.uploadTicketUidValue

    this.ticketIdValue = ticketId
    this.ticketUidValue = ticketUid

    this.ticketDisplayTarget.textContent = ticketUid
    this.ticketInputTarget.value = ticketId

    // Show ticket info box, hide search input
    if (this.hasTicketInfoTarget) this.ticketInfoTarget.classList.remove("hidden")
    if (this.hasTicketSelectorTarget) this.ticketSelectorTarget.classList.add("hidden")

    this.modalTarget.classList.remove("hidden")
    this.resetForm()
  }

  showTicketSelector(event) {
    event.preventDefault()

    // Hide ticket info, show search input
    if (this.hasTicketInfoTarget) this.ticketInfoTarget.classList.add("hidden")
    if (this.hasTicketSelectorTarget) this.ticketSelectorTarget.classList.remove("hidden")

    // Clear and focus search input
    if (this.hasTicketSearchTarget) {
      this.ticketSearchTarget.value = ""
      this.ticketSearchTarget.focus()
    }

    // Clear ticket selection
    this.ticketInputTarget.value = ""
    this.updateSubmitButton()
  }

  openModalForAnyTicket(event) {
    event.preventDefault()

    // Clear ticket info
    this.ticketIdValue = null
    this.ticketUidValue = null
    this.ticketInputTarget.value = ""

    // Show ticket selector, hide ticket info
    if (this.hasTicketInfoTarget) this.ticketInfoTarget.classList.add("hidden")
    if (this.hasTicketSelectorTarget) this.ticketSelectorTarget.classList.remove("hidden")

    this.modalTarget.classList.remove("hidden")
    this.resetForm()

    // Load recent tickets immediately
    this.loadRecentTickets()

    // Focus on ticket search
    if (this.hasTicketSearchTarget) {
      setTimeout(() => this.ticketSearchTarget.focus(), 100)
    }
  }

  async loadRecentTickets() {
    try {
      const response = await fetch('/tickets_autocomplete.json?q=', {
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector("[name='csrf-token']").content
        }
      })

      if (response.ok) {
        const tickets = await response.json()
        const resultsContainer = document.getElementById("ticket-results")
        this.displayTicketResults(tickets.slice(0, 10), resultsContainer) // Show top 10
      }
    } catch (error) {
      console.error("Failed to load recent tickets:", error)
    }
  }

  handleTicketSelect(event) {
    const selectedOption = event.target.selectedOptions[0]
    if (selectedOption && selectedOption.value) {
      this.ticketInputTarget.value = selectedOption.value
      this.submitButtonTarget.disabled = !this.selectedFile
    } else {
      this.ticketInputTarget.value = ""
      this.submitButtonTarget.disabled = true
    }
  }

  async searchTickets(event) {
    const query = event.target.value.trim()

    // Clear previous timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }

    const resultsContainer = document.getElementById("ticket-results")

    if (query.length < 2) {
      resultsContainer.classList.add("hidden")
      resultsContainer.innerHTML = ""
      return
    }

    // Debounce search
    this.searchTimeout = setTimeout(async () => {
      try {
        const response = await fetch(`/tickets_autocomplete.json?q=${encodeURIComponent(query)}`, {
          headers: {
            "Accept": "application/json",
            "X-CSRF-Token": document.querySelector("[name='csrf-token']").content
          }
        })

        if (response.ok) {
          const tickets = await response.json()
          this.displayTicketResults(tickets, resultsContainer)
        }
      } catch (error) {
        console.error("Ticket search error:", error)
      }
    }, 300)
  }

  displayTicketResults(tickets, container) {
    if (!tickets || tickets.length === 0) {
      container.innerHTML = '<div class="p-3 text-sm text-gray-500 dark:text-gray-400">No tickets found</div>'
      container.classList.remove("hidden")
      return
    }

    const controller = this
    container.innerHTML = tickets.map(ticket => {
      const summary = ticket.summary || 'No summary'
      const truncatedSummary = summary.length > 60 ? summary.substring(0, 60) + '...' : summary

      return `
      <div
        class="p-3 hover:bg-blue-50 dark:hover:bg-gray-600 cursor-pointer border-b dark:border-gray-600 last:border-b-0 transition-colors"
        data-ticket-id="${ticket.id}"
        data-ticket-uid="${ticket.uid}"
        data-ticket-summary="${this.escapeHtml(summary)}"
        data-action="click->upload#selectTicket"
      >
        <div class="flex items-start justify-between">
          <div class="flex-1 min-w-0">
            <div class="text-sm font-semibold text-blue-600 dark:text-blue-400">${ticket.uid}</div>
            <div class="text-xs text-gray-600 dark:text-gray-400 mt-1 truncate" title="${this.escapeHtml(summary)}">${this.escapeHtml(truncatedSummary)}</div>
          </div>
          <svg class="w-4 h-4 text-gray-400 ml-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
          </svg>
        </div>
      </div>
    `}).join('')

    container.classList.remove("hidden")
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  selectTicket(event) {
    const ticketDiv = event.currentTarget
    const ticketId = ticketDiv.dataset.ticketId
    const ticketUid = ticketDiv.dataset.ticketUid
    const ticketSummary = ticketDiv.dataset.ticketSummary

    // Set the ticket
    this.ticketInputTarget.value = ticketId
    this.ticketIdValue = ticketId
    this.ticketUidValue = ticketUid

    // Update search input with UID and summary
    if (this.hasTicketSearchTarget) {
      this.ticketSearchTarget.value = `${ticketUid} - ${ticketSummary}`
    }

    // Hide results
    document.getElementById('ticket-results').classList.add('hidden')

    // Update submit button
    this.updateSubmitButton()
  }

  updateSubmitButton() {
    const hasTicket = this.ticketInputTarget.value && this.ticketInputTarget.value !== ""
    const hasFile = this.selectedFile !== null
    this.submitButtonTarget.disabled = !(hasTicket && hasFile)
  }

  // Handle ticket selection event to update submit button
  addEventListener(event) {
    if (event.type === 'ticket-selected') {
      this.submitButtonTarget.disabled = !this.selectedFile
    }
  }

  closeModal(event) {
    if (event) event.preventDefault()
    this.modalTarget.classList.add("hidden")
    this.resetForm()
  }

  clickFileInput(event) {
    // Clicks on the label/input already open the picker natively; re-triggering
    // here bubbles back into this handler and browsers block the dialog.
    if (event.target.closest("label")) return
    this.fileInputTarget.click()
  }

  // Extension list is sourced from Uploads::Process::ALLOWED_EXTENSIONS via the data attribute,
  // so browser validation stays in sync with the server without manual updates.
  isValidUploadFile(file) {
    if (VALID_MIME_PREFIXES.some(type => file.type.startsWith(type))) return true
    const extensions = this.allowedExtensionsValue
      ? this.allowedExtensionsValue.split(',').map(e => e.trim().toLowerCase())
      : []
    return extensions.some(ext => file.name.toLowerCase().endsWith(ext))
  }

  handleFileSelect(event) {
    const file = event.target.files[0]
    if (file) {
      // Validate file type
      if (!this.isValidUploadFile(file)) {
        this.showError('Please select a supported file type (image, video, PDF, or common document/spreadsheet formats)')
        this.fileInputTarget.value = ""
        return
      }

      // Validate file size (100MB)
      const maxSize = 100 * 1024 * 1024 // 100MB in bytes
      if (file.size > maxSize) {
        this.showError('File size exceeds 100MB limit')
        this.fileInputTarget.value = ""
        return
      }

      this.setSelectedFile(file)
    }
  }

  handleDragOver(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dropzoneTarget.classList.add("border-blue-500", "bg-blue-100", "dark:bg-blue-900", "scale-105")
    this.dropzoneTarget.classList.remove("border-gray-300", "dark:border-gray-600", "bg-gray-50", "dark:bg-gray-800")
  }

  handleDragLeave(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dropzoneTarget.classList.remove("border-blue-500", "bg-blue-100", "dark:bg-blue-900", "scale-105")
    this.dropzoneTarget.classList.add("border-gray-300", "dark:border-gray-600", "bg-gray-50", "dark:bg-gray-800")
  }

  handleDrop(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dropzoneTarget.classList.remove("border-blue-500", "bg-blue-100", "dark:bg-blue-900", "scale-105")
    this.dropzoneTarget.classList.add("border-gray-300", "dark:border-gray-600", "bg-gray-50", "dark:bg-gray-800")

    const file = event.dataTransfer.files[0]
    if (file) {
      // Validate file type
      if (!this.isValidUploadFile(file)) {
        this.showError('Please select a supported file type (image, video, PDF, or common document/spreadsheet formats)')
        return
      }

      // Validate file size (100MB)
      const maxSize = 100 * 1024 * 1024 // 100MB in bytes
      if (file.size > maxSize) {
        this.showError('File size exceeds 100MB limit')
        return
      }

      this.setSelectedFile(file)
      // Update the file input
      const dataTransfer = new DataTransfer()
      dataTransfer.items.add(file)
      this.fileInputTarget.files = dataTransfer.files
    }
  }

  setSelectedFile(file) {
    this.selectedFile = file

    // Format file size
    const formatFileSize = (bytes) => {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
    }

    this.fileNameTarget.textContent = `${file.name} (${formatFileSize(file.size)})`
    this.filePreviewTarget.classList.remove("hidden")
    this.updateSubmitButton()
  }

  removeFile(event) {
    event.preventDefault()
    this.selectedFile = null
    this.fileInputTarget.value = ""
    this.filePreviewTarget.classList.add("hidden")
    this.submitButtonTarget.disabled = true
  }

  async handleSubmit(event) {
    event.preventDefault()

    if (!this.selectedFile) {
      alert("Please select a file to upload")
      return
    }

    if (!this.ticketInputTarget.value) {
      alert("Please select a ticket")
      return
    }

    this.submitButtonTarget.disabled = true
    this.progressContainerTarget.classList.remove("hidden")
    this.updateProgress(0, "Uploading file...")

    const formData = new FormData()
    formData.append("upload[ticket_id]", this.ticketInputTarget.value)
    formData.append("upload[file]", this.selectedFile)

    // Add comment if provided
    if (this.hasCommentInputTarget && this.commentInputTarget.value.trim()) {
      formData.append("upload[comment]", this.commentInputTarget.value.trim())
    }

    try {
      const response = await fetch("/uploads", {
        method: "POST",
        body: formData,
        headers: {
          "X-CSRF-Token": document.querySelector("[name='csrf-token']").content
        }
      })

      if (response.ok) {
        const data = await response.json()
        this.updateProgress(100, "Upload complete!")

        setTimeout(() => {
          this.showSuccess(data.message || "File uploaded and posted to YouTrack successfully!")
          this.closeModal()
        }, 1000)
      } else {
        const error = await response.json()
        throw new Error(error.message || "Upload failed")
      }
    } catch (error) {
      console.error("Upload error:", error)
      this.showError(error.message || "An error occurred during upload")
      this.submitButtonTarget.disabled = false
      this.progressContainerTarget.classList.add("hidden")
    }
  }

  updateProgress(percentage, message) {
    this.progressBarTarget.style.width = `${percentage}%`
    this.statusMessageTarget.textContent = message
  }

  showSuccess(message) {
    // You can implement a toast notification here
    alert(message)
  }

  showError(message) {
    // You can implement a toast notification here
    alert(`Error: ${message}`)
  }

  resetForm() {
    this.selectedFile = null
    this.fileInputTarget.value = ""
    this.filePreviewTarget.classList.add("hidden")
    this.progressContainerTarget.classList.add("hidden")
    this.submitButtonTarget.disabled = true
    this.progressBarTarget.style.width = "0%"

    // Clear comment
    if (this.hasCommentInputTarget) {
      this.commentInputTarget.value = ""
    }

    // Reset to write tab
    if (this.hasWriteTabTarget && this.hasCommentTabTarget) {
      this.showWriteTab()
    }

    // Clear ticket search
    if (this.hasTicketSearchTarget) {
      this.ticketSearchTarget.value = ""
    }
  }

  showWriteTab(event) {
    if (event) event.preventDefault()

    if (!this.hasWriteTabTarget || !this.hasCommentTabTarget) return

    // Show write tab, hide preview
    this.commentInputTarget.classList.remove("hidden")
    if (this.hasCommentPreviewTarget) {
      this.commentPreviewTarget.classList.add("hidden")
    }

    // Update tab styling
    this.writeTabTarget.classList.add("border-blue-500", "text-blue-600", "dark:border-blue-400", "dark:text-blue-400")
    this.writeTabTarget.classList.remove("border-transparent", "text-gray-500", "dark:text-gray-400")

    if (this.hasCommentTabTarget) {
      this.commentTabTarget.classList.remove("border-blue-500", "text-blue-600", "dark:border-blue-400", "dark:text-blue-400")
      this.commentTabTarget.classList.add("border-transparent", "text-gray-500", "dark:text-gray-400")
    }
  }

  showPreviewTab(event) {
    if (event) event.preventDefault()

    if (!this.hasCommentPreviewTarget || !this.hasCommentTabTarget) return

    // Hide write tab, show preview
    this.commentInputTarget.classList.add("hidden")
    this.commentPreviewTarget.classList.remove("hidden")

    // Update preview content
    this.updatePreview()

    // Update tab styling
    this.commentTabTarget.classList.add("border-blue-500", "text-blue-600", "dark:border-blue-400", "dark:text-blue-400")
    this.commentTabTarget.classList.remove("border-transparent", "text-gray-500", "dark:text-gray-400")

    if (this.hasWriteTabTarget) {
      this.writeTabTarget.classList.remove("border-blue-500", "text-blue-600", "dark:border-blue-400", "dark:text-blue-400")
      this.writeTabTarget.classList.add("border-transparent", "text-gray-500", "dark:text-gray-400")
    }
  }

  updatePreview() {
    if (!this.hasCommentPreviewTarget) return

    const markdownText = this.commentInputTarget.value.trim()

    if (!markdownText) {
      this.commentPreviewTarget.innerHTML = '<p class="text-gray-400 italic">Nothing to preview</p>'
      return
    }

    // Simple markdown rendering (you can replace with a library like marked.js if needed)
    const html = this.renderMarkdown(markdownText)
    this.commentPreviewTarget.innerHTML = html
  }

  renderMarkdown(text) {
    // Basic markdown rendering - you can enhance this or use a library
    let html = text

    // Headers
    html = html.replace(/^### (.*$)/gim, '<h3 class="text-lg font-semibold mt-3 mb-2">$1</h3>')
    html = html.replace(/^## (.*$)/gim, '<h2 class="text-xl font-semibold mt-4 mb-2">$1</h2>')
    html = html.replace(/^# (.*$)/gim, '<h1 class="text-2xl font-bold mt-4 mb-3">$1</h1>')

    // Bold
    html = html.replace(/\*\*(.*?)\*\*/gim, '<strong class="font-bold">$1</strong>')

    // Italic
    html = html.replace(/\*(.*?)\*/gim, '<em class="italic">$1</em>')

    // Links
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/gim, '<a href="$2" class="text-blue-600 hover:underline" target="_blank">$1</a>')

    // Code blocks
    html = html.replace(/```([\s\S]*?)```/gim, '<pre class="bg-gray-100 dark:bg-gray-700 p-3 rounded mt-2 mb-2 overflow-x-auto"><code>$1</code></pre>')

    // Inline code
    html = html.replace(/`([^`]+)`/gim, '<code class="bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded text-sm">$1</code>')

    // Lists
    html = html.replace(/^\* (.*$)/gim, '<li class="ml-4">$1</li>')
    html = html.replace(/^- (.*$)/gim, '<li class="ml-4">$1</li>')

    // Line breaks
    html = html.replace(/\n\n/g, '</p><p class="mt-2">')
    html = html.replace(/\n/g, '<br>')

    // Wrap in paragraph
    html = '<div class="prose dark:prose-invert max-w-none"><p>' + html + '</p></div>'

    return html
  }
}
